import { createSelector } from 'reselect'
import { selectAnnualTaxNotAvailable } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import {
  generalCUBKDetails,
  getBookkeepingSectionDetails,
  getBudgetingSectionDetails,
  getGroupPracticeSectionDetails,
  getReportingSectionDetails,
  getSCorpSectionDetails,
  getSupportSectionDetails,
  getTaxSectionDetails,
} from '@/features/user/signup/choose-plan/helpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import { planTierServicesFor } from '@/constants/productFeatureConstants'
import { ProductFeatureId } from '@/actions/permissionActions'
import { selectPayrollPrices } from '@/features/payroll/payrollPrice.selectors'

type ProductDescriptionOptions = {
  sections: (
    | 'taxes'
    | 'bookkeeping'
    | 'catch_up_bookkeeping'
    | 'budget'
    | 'scorp'
    | 'group'
    | 'support'
    | 'extended_support'
    | 'reporting'
  )[]
  permissions?: ProductFeatureId[]
  /** The advertised product's tier, which decides the services no entitlement grants. */
  tier?: string | null
  copyFromTier?: boolean
  isLitePlanEnabled?: boolean
  payrollPromoText?: string
}

export const selectProductDescription = createSelector(
  getCurrentUser,
  selectAnnualTaxNotAvailable,
  selectCurrentAnnualTaxYear,
  selectPayrollPrices,
  (_: unknown, options: ProductDescriptionOptions) => options,
  (currentUser, isPastCutoff, currentAnnualTaxYear, payrollPrices, options) => {
    const {
      sections,
      isLitePlanEnabled = false,
      permissions = [],
      tier,
      copyFromTier = false,
      payrollPromoText,
    } = options
    if (
      !currentUser?.financialProfile?.taxEntityType ||
      !currentUser?.financialProfile?.practiceType
    ) {
      return null
    }

    const { practiceType, taxEntityType } = currentUser.financialProfile

    const isScorp = copyFromTier
      ? taxEntityType === TAX_ENTITY_TYPES.form_1120_s
      : taxEntityType === TAX_ENTITY_TYPES.form_1120_s ||
        taxEntityType === TAX_ENTITY_TYPES.form_1120
    const isGroupPractice = practiceType === 'group'

    // Off the flag, the services come from the advertised product's own grants under the
    // conditions each section applied before the tier map: group practice for an S corp only.
    const grantedServices = [
      ProductFeatureId.sCorpEligibilitySupport,
      ProductFeatureId.sCorpComplianceSupport,
      ...(isScorp ? [ProductFeatureId.groupPracticeSupport] : []),
    ].filter((service) => permissions.includes(service))

    const planTierServices = copyFromTier
      ? planTierServicesFor({ tier, isLegacy: false, isScorp, isGroupPractice })
      : grantedServices

    const nextTaxYear = Number(currentAnnualTaxYear) + 1

    const taxDetails = getTaxSectionDetails({
      isScorp,
      isLitePlanEnabled,
      permissions,
      planTierServices,
      isPastCutoff: isPastCutoff ?? false,
      nextTaxYear,
    })

    const scorpDetails = getSCorpSectionDetails({
      permissions,
      planTierServices,
      payrollPromoText,
      payrollPrices,
    })

    const bkDetails = getBookkeepingSectionDetails({
      isLitePlanEnabled,
    })

    const budgetingDetails = getBudgetingSectionDetails({
      isGroup: isGroupPractice,
      isScorp,
      payrollPromoText,
      payrollPrices,
    })

    const reportingDetails = getReportingSectionDetails()

    const supportDetails = getSupportSectionDetails({ isLitePlanEnabled })

    const groupPracticeDetails = getGroupPracticeSectionDetails({
      isLitePlanEnabled,
      planTierServices,
      payrollPrices,
    })

    return [
      {
        title: 'Bookkeeping',
        isVisible: sections.includes('bookkeeping') && bkDetails.length > 0,
        details: bkDetails,
        section: 'bookkeeping',
      },
      {
        title: 'Taxes',
        isVisible: sections.includes('taxes') && taxDetails.length > 0,
        details: taxDetails,
        section: 'taxes',
      },
      {
        title: 'Catch up Bookkeeping',
        isVisible:
          sections.includes('catch_up_bookkeeping') &&
          generalCUBKDetails.length > 0,
        details: generalCUBKDetails,
        section: 'catch_up_bookkeeping',
      },
      {
        title: 'Budgeting & Tracking',
        isVisible:
          !isLitePlanEnabled &&
          sections.includes('budget') &&
          budgetingDetails.length > 0,
        details: budgetingDetails,
        section: 'budget',
      },
      {
        title: 'Reports and Tracking',
        isVisible:
          isLitePlanEnabled &&
          sections.includes('reporting') &&
          reportingDetails.length > 0,
        details: reportingDetails,
        section: 'reporting',
      },
      {
        title: 'S Corporation Compliance',
        isVisible: sections.includes('scorp') && scorpDetails.length > 0,
        details: scorpDetails,
        section: 'scorp',
      },
      {
        title: 'Group Practice Support',
        isVisible:
          isLitePlanEnabled &&
          sections.includes('group') &&
          groupPracticeDetails.length > 0,
        details: groupPracticeDetails,
        section: 'group',
      },
      {
        title: 'Human Support',
        isVisible:
          isLitePlanEnabled &&
          (sections.includes('support') ||
            sections.includes('extended_support')) &&
          supportDetails.length > 0,
        details: supportDetails,
        section: 'support',
      },
    ]
  }
)
